/** Presentation-only scales; never used to classify forecast weather codes.
 * Input values are inches, before any user-selected unit conversion. */
export function validRain(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

export function rainIntensity(value: number | null | undefined) {
  const rate = validRain(value);
  if (rate === null) return { label: "Unavailable", fill: 0 };
  // Piecewise instrument scale gives the light-rain range readable space.
  const fill =
    rate <= 0.1
      ? rate / 0.1 / 3
      : rate <= 0.3
        ? 1 / 3 + (rate - 0.1) / 0.2 / 3
        : 2 / 3 + Math.min((rate - 0.3) / 0.7, 1) / 3;
  return {
    label:
      rate === 0
        ? "Dry"
        : rate < 0.1
          ? "Light"
          : rate < 0.3
            ? "Moderate"
            : "Heavy",
    fill: fill * 100,
  };
}

export function rainComparison(
  observedValue: number | null | undefined,
  forecastValue: number | null | undefined,
) {
  const observed = validRain(observedValue);
  const forecast = validRain(forecastValue);
  const ratio =
    observed !== null && forecast !== null && forecast > 0
      ? observed / forecast
      : null;
  return {
    observed,
    forecast,
    percent: ratio === null ? null : Math.round(ratio * 100),
    fill: ratio === null ? 0 : Math.min(ratio * 100, 100),
    exceeded: observed !== null && forecast !== null && observed > forecast,
  };
}

/** Zero is an observation; missing values must never imply dry weather. */
export function rainPanelState(values: {
  rate?: number | null;
  hourly?: number | null;
  today?: number | null;
  storm?: number | null;
  last24?: number | null;
  forecast?: number | null;
}): "unavailable" | "dry" | "measurements" {
  const observed = [
    values.rate,
    values.hourly,
    values.today,
    values.storm,
    values.last24,
  ].map(validRain);
  // Forecast rain is shown separately and never implies a station observation.
  if (observed.some((value) => value !== null && value > 0))
    return "measurements";
  if (validRain(values.today) === 0) return "dry";
  return observed.every((value) => value === null)
    ? "unavailable"
    : "measurements";
}
